import { isInsideRanch } from '@shared/locality';
import { selectByCategory, type FilterablePlace } from './placeFilter';

/**
 * Map-tab predicates, extracted so tests hit the real filter — not a
 * reimplementation that would stay green if the page drifted.
 *
 * The Map is the spatial view of in-Ranch places. Places stays the
 * list/search home (inside + outside). Category chips on the map reuse
 * `selectByCategory` so Fitness still includes `exercise`-tagged parks.
 */

export const MAP_CORE_RADIUS_MILES = 4;

export interface MapCategory {
  key: string;
  label: string;
  cssVar: string;
}

export const MAP_CATEGORIES: MapCategory[] = [
  { key: 'all', label: 'All', cssVar: '--map-cat-civic' },
  { key: 'food', label: 'Food', cssVar: '--map-cat-food' },
  { key: 'grocery', label: 'Grocery', cssVar: '--map-cat-grocery' },
  { key: 'medical', label: 'Medical', cssVar: '--map-cat-medical' },
  { key: 'fitness', label: 'Fitness', cssVar: '--map-cat-fitness' },
  { key: 'recreation', label: 'Parks', cssVar: '--map-cat-recreation' },
  { key: 'shopping', label: 'Shopping', cssVar: '--map-cat-shopping' },
  { key: 'home_improvement', label: 'Hardware', cssVar: '--map-cat-home-improvement' },
  { key: 'services', label: 'Services', cssVar: '--map-cat-services' },
  { key: 'attraction', label: 'Attractions', cssVar: '--map-cat-attraction' },
  { key: 'babcock_ranch', label: 'Town', cssVar: '--map-cat-civic' },
];

const CATEGORY_KEYS = new Set(MAP_CATEGORIES.map((c) => c.key));

export function mapCategoryMeta(category: string): MapCategory {
  return MAP_CATEGORIES.find((c) => c.key === category) ?? MAP_CATEGORIES[0];
}

export type MappablePlace = FilterablePlace & {
  latitude: number;
  longitude: number;
  name: string;
  geographic_ring?: string | null;
  slug?: string | null;
  aliases?: string[];
  distance_from_home_miles?: number | null;
};

export function isMappablePlace(place: MappablePlace): boolean {
  return isInsideRanch(place) && Number.isFinite(place.latitude) && Number.isFinite(place.longitude);
}

export function filterMapPlaces<T extends MappablePlace>(
  places: readonly T[],
  opts: { category: string; query: string },
): T[] {
  const mappable = places.filter(isMappablePlace);
  const byCat =
    opts.category === 'all' || !CATEGORY_KEYS.has(opts.category)
      ? mappable
      : selectByCategory(mappable, opts.category);
  const q = opts.query.trim().toLowerCase();
  if (!q) return byCat;
  return byCat.filter((p) => {
    if (p.name.toLowerCase().includes(q)) return true;
    if (p.tags.some((t) => t.toLowerCase().includes(q))) return true;
    return (p.aliases ?? []).some((a) => a.toLowerCase().includes(q));
  });
}

export interface MapBounds {
  south: number;
  west: number;
  north: number;
  east: number;
}

export function placeInBounds(place: { latitude: number; longitude: number }, bounds: MapBounds): boolean {
  return (
    place.latitude >= bounds.south &&
    place.latitude <= bounds.north &&
    place.longitude >= bounds.west &&
    place.longitude <= bounds.east
  );
}

export function placesInBounds<T extends { latitude: number; longitude: number }>(
  places: readonly T[],
  bounds: MapBounds | null,
): T[] {
  if (!bounds) return [...places];
  return places.filter((p) => placeInBounds(p, bounds));
}

/**
 * Nearby list: when the user typed a query, show every match (search is
 * not silently clipped by the current viewport). With no query, list the
 * filtered pins currently in view — lightweight "this area" without a
 * server refetch.
 */
export function nearbyList<T extends MappablePlace>(
  filtered: readonly T[],
  bounds: MapBounds | null,
  query: string,
): T[] {
  const list = query.trim() ? [...filtered] : placesInBounds(filtered, bounds);
  return list.sort((a, b) => {
    const da = a.distance_from_home_miles ?? Number.POSITIVE_INFINITY;
    const db = b.distance_from_home_miles ?? Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    return a.name.localeCompare(b.name);
  });
}

/** ~11 m grid — same key the old fan-out used, so Founder's Square still groups. */
export function colocatedKey(lat: number, lon: number): string {
  return `${lat.toFixed(4)},${lon.toFixed(4)}`;
}

export function colocatedGroups<T extends { latitude: number; longitude: number }>(places: readonly T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const p of places) {
    const key = colocatedKey(p.latitude, p.longitude);
    const arr = groups.get(key);
    if (arr) arr.push(p);
    else groups.set(key, [p]);
  }
  return [...groups.values()].filter((g) => g.length > 1);
}

export function parseMapFilters(search: string): { category: string; query: string } {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const cat = q.get('category') ?? '';
  return {
    category: CATEGORY_KEYS.has(cat) ? cat : 'all',
    query: q.get('q') ?? '',
  };
}

export function serializeMapFilters(category: string, query: string): string {
  const q = new URLSearchParams();
  if (category !== 'all' && CATEGORY_KEYS.has(category)) q.set('category', category);
  const trimmed = query.trim();
  if (trimmed) q.set('q', trimmed);
  return q.toString();
}

export function isCorePlace(place: { distance_from_home_miles?: number | null }): boolean {
  return (place.distance_from_home_miles ?? 0) <= MAP_CORE_RADIUS_MILES;
}

export function activeMapCategories<T extends FilterablePlace>(places: readonly T[]): MapCategory[] {
  return MAP_CATEGORIES.filter((c) => {
    if (c.key === 'all') return true;
    return selectByCategory([...places], c.key).length > 0;
  });
}
